/**
 * Phase 15.6Q + iter_226 · Phase 22 — RGM Marketplace (2-tab shell).
 *
 * Tabs:
 *   • Beat Marketplace  → original Beat Marketplace UI (default)
 *   • Singles Store     → direct-to-consumer singles (dark-launched)
 *
 * The Singles tab renders only when either (a) the backend kill switch
 * `singles.enabled = true`, or (b) the viewer is an admin/founder (so
 * we can verify checkout / settlement / refund flows without exposing
 * purchases to regular artists). Kept off by default at launch.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import ProducerLanes from "@/components/ProducerLanes";
import BeatCard from "@/components/BeatCard";
import BeatOfTheWeekHero from "@/components/BeatOfTheWeekHero";
import SinglesStorePage from "@/pages/SinglesStorePage";
import { useCurrentUser } from "@/lib/useCurrentUser";

const SORT_OPTIONS = [
  { key: "featured", label: "Featured" },
  { key: "new", label: "Newest" },
  { key: "plays", label: "Most Played" },
  { key: "price_low", label: "Price ↑" },
  { key: "price_high", label: "Price ↓" },
];

const BeatMarketplaceTab = ({ currentUser }) => {
  const navigate = useNavigate();
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [search, setSearch] = useState("");
  const [license, setLicense] = useState("");
  const [bpmRange, setBpmRange] = useState([null, null]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = { sort, limit: 60 };
    if (search.trim()) params.search = search.trim();
    if (license) params.license_type = license;
    if (bpmRange[0]) params.bpm_min = bpmRange[0];
    if (bpmRange[1]) params.bpm_max = bpmRange[1];
    api.get("/beats", { params })
      .then((r) => { if (alive) setBeats(r.data?.beats || []); })
      .catch(() => { if (alive) setBeats([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sort, search, license, bpmRange]);

  const featured = useMemo(() => beats.filter((b) => b.featured), [beats]);

  return (
    <>
      {/* Filter bar */}
      <div className="border-b border-gray-900 bg-black/40 sticky top-16 z-20 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap gap-3 items-center">
          <Input
            type="search"
            placeholder="Search title, tag, genre…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-gray-950/60 border-purple-500/20 text-white max-w-xs"
            data-testid="beat-search-input"
          />
          <select
            value={license}
            onChange={(e) => setLicense(e.target.value)}
            className="bg-gray-950/60 border border-purple-500/20 rounded-md text-white px-3 py-2 text-sm"
            data-testid="beat-license-select"
          >
            <option value="">Any license</option>
            <option value="Free">Free</option>
            <option value="Lease">Lease</option>
            <option value="Premium">Premium</option>
            <option value="Exclusive">Exclusive</option>
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-gray-950/60 border border-purple-500/20 rounded-md text-white px-3 py-2 text-sm"
            data-testid="beat-sort-select"
          >
            {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          <div className="flex items-center gap-2 text-xs text-purple-200">
            <span>BPM</span>
            <Input type="number" placeholder="min" value={bpmRange[0] || ""}
              onChange={(e) => setBpmRange([e.target.value ? Number(e.target.value) : null, bpmRange[1]])}
              className="w-20 bg-gray-950/60 border-purple-500/20 text-white text-xs"
              data-testid="beat-bpm-min" />
            <span>–</span>
            <Input type="number" placeholder="max" value={bpmRange[1] || ""}
              onChange={(e) => setBpmRange([bpmRange[0], e.target.value ? Number(e.target.value) : null])}
              className="w-20 bg-gray-950/60 border-purple-500/20 text-white text-xs"
              data-testid="beat-bpm-max" />
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <BeatOfTheWeekHero currentUserId={currentUser?.user_id} />
        <ProducerLanes />
        {loading ? (
          <div className="text-center py-20 text-gray-400" data-testid="beats-loading">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-purple-500" />
            <p className="mt-4 text-sm uppercase tracking-widest">Loading beats…</p>
          </div>
        ) : beats.length === 0 ? (
          <Card className="p-12 text-center text-gray-400 bg-gray-900/40 border-gray-800" data-testid="beats-empty">
            <div className="text-6xl mb-4">🎹</div>
            <p className="text-lg font-semibold text-white">No beats match your filters.</p>
            <p className="text-sm mt-2">Adjust the filters or be the first to upload — every RGM producer is welcome.</p>
          </Card>
        ) : (
          <>
            {sort === "featured" && featured.length > 0 && (
              <section className="mb-10" data-testid="beats-featured-section">
                <h2 className="text-2xl font-black text-white mb-4">⭐ Featured Beats</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {featured.slice(0, 5).map((b) => (
                    <BeatCard
                      key={b.beat_id} beat={b}
                      currentUserId={currentUser?.user_id}
                      onContact={(beat) => navigate(`/beats/${beat.beat_id}#contact`)}
                      onHidden={(bid) => setBeats((bs) => bs.filter((x) => x.beat_id !== bid))}
                    />
                  ))}
                </div>
              </section>
            )}
            <section data-testid="beats-grid-section">
              <h2 className="text-2xl font-black text-white mb-4">
                {sort === "featured" ? "All Beats" : SORT_OPTIONS.find((s) => s.key === sort)?.label}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="beats-grid">
                {beats.map((b) => (
                  <BeatCard
                    key={b.beat_id} beat={b}
                    currentUserId={currentUser?.user_id}
                    onContact={(beat) => navigate(`/beats/${beat.beat_id}#contact`)}
                    onHidden={(bid) => setBeats((bs) => bs.filter((x) => x.beat_id !== bid))}
                  />
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </>
  );
};

const BeatsMarketplacePage = () => {
  const { currentUser } = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const [access, setAccess] = useState(null); // {enabled, admin_bypass, can_view, can_sell}

  // Poll access status once so we know whether to show the Singles tab.
  useEffect(() => {
    api.get("/singles/access/status")
      .then((r) => setAccess(r.data))
      .catch(() => setAccess({ enabled: false, admin_bypass: false, can_view: false, can_sell: false }));
  }, []);

  const singlesVisible = !!(access && access.can_view);
  const initial = params.get("tab") === "singles" && singlesVisible ? "singles" : "beats";
  const [tab, setTab] = useState(initial);
  useEffect(() => {
    // Keep URL in sync so admins can share a direct link to the tab.
    const cur = params.get("tab");
    if (tab === "singles" && cur !== "singles") setParams({ tab: "singles" }, { replace: true });
    if (tab === "beats" && cur === "singles") setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="beats-marketplace-page">
      <SharedNavigation currentUser={currentUser} activePage="beats-market" />

      <header className="pt-24 pb-6 px-4 sm:px-6 lg:px-8 border-b border-gray-900 bg-gradient-to-b from-purple-950/30 via-black to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">RGM Marketplace</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight mt-2 leading-none">
                🛒 Marketplace
              </h1>
              <p className="text-gray-400 mt-3 text-sm sm:text-base max-w-2xl">
                {tab === "beats"
                  ? "Discover beats from RGM Studio producers. Lease, download, or support creators with secure checkout. Producers keep 90% of beat sales."
                  : "Direct-to-consumer singles from RGM Studio artists. Personal-use downloads, secure preview, and 7-day earnings hold."}
              </p>
              {tab === "singles" && access && !access.enabled && access.admin_bypass && (
                <p
                  className="mt-3 inline-block text-[11px] font-mono uppercase tracking-widest text-amber-300 border border-amber-500/40 rounded-full px-3 py-1 bg-amber-500/5"
                  data-testid="singles-admin-bypass-banner"
                >
                  🛡 Admin preview · <code>singles.enabled=false</code>
                </p>
              )}
            </div>
            {currentUser && (
              <div className="flex flex-wrap gap-2">
                <Link to="/submit-beat">
                  <Button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white" data-testid="upload-beat-cta">
                    + Upload a Beat
                  </Button>
                </Link>
                <Link to="/beat-playlists">
                  <Button variant="outline" className="border-purple-500/40 text-purple-100" data-testid="beat-playlists-cta">
                    🎼 Beat Playlists
                  </Button>
                </Link>
                <Link to="/producer">
                  <Button variant="outline" className="border-purple-500/40 text-purple-100" data-testid="producer-dashboard-cta">
                    Producer Dashboard
                  </Button>
                </Link>
                {singlesVisible && access?.can_sell && (
                  <Link to="/dashboard">
                    <Button variant="outline" className="border-emerald-500/40 text-emerald-100" data-testid="sell-track-cta">
                      💿 Sell a Track
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="mt-6 flex gap-2 border-b border-white/5" data-testid="marketplace-tabs">
            <button
              onClick={() => setTab("beats")}
              className={`px-4 py-2 text-sm font-black uppercase tracking-widest transition-colors border-b-2 ${tab === "beats" ? "text-white border-purple-500" : "text-gray-500 border-transparent hover:text-gray-300"}`}
              data-testid="tab-beat-marketplace"
            >
              🎹 Beat Marketplace
            </button>
            {singlesVisible && (
              <button
                onClick={() => setTab("singles")}
                className={`px-4 py-2 text-sm font-black uppercase tracking-widest transition-colors border-b-2 ${tab === "singles" ? "text-white border-emerald-500" : "text-gray-500 border-transparent hover:text-gray-300"}`}
                data-testid="tab-singles-store"
              >
                💿 Singles Store
                {!access.enabled && access.admin_bypass && (
                  <span className="ml-2 text-[9px] font-mono text-amber-300 align-middle">DARK</span>
                )}
              </button>
            )}
          </div>
        </div>
      </header>

      {tab === "beats" ? (
        <BeatMarketplaceTab currentUser={currentUser} />
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <SinglesStorePage />
        </div>
      )}
    </div>
  );
};

export default BeatsMarketplacePage;
